import React, { useEffect, useMemo, useState } from 'react';
import { Pantallas } from './services/api';
import { cls, Modal } from './components/ui';
import GroupCard from './components/GroupCard';
import PlaylistEditor from './components/PlaylistEditor';
import GroupDesignEditor from './components/GroupDesignEditor';
import GroupMessagesEditor from './components/GroupMessagesEditor';
import TvsSection from './components/TvsSection';

export default function GroupsAdmin() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [view, setView] = useState('grupos');

  const [q, setQ] = useState('');
  const [sort, setSort] = useState('recent'); // recent | name | videos

  const [open, setOpen] = useState(false); // drawer
  const [selected, setSelected] = useState(null);
  const [playlist, setPlaylist] = useState([]);

  const [showCreate, setShowCreate] = useState(false);
  const [newGroup, setNewGroup] = useState({ nombre: '', diseno: { fecha: true, reloj: true } });

  // === API calls ===
  const fetchGroups = async () => {
    try {
      setLoading(true);
      const data = await Pantallas.getGroups();
      setGroups(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlaylist = async (groupId) => {
    const data = await Pantallas.getGroupPlaylist(groupId, false);
    setPlaylist(Array.isArray(data) ? data : []);
  };

  const createGroup = async () => {
    if (!newGroup.nombre) {
      alert('El nombre es requerido');
      return;
    }
    try {
      await Pantallas.createGroup(newGroup);
      setShowCreate(false);
      setNewGroup({ nombre: '', diseno: { fecha: true, reloj: true } });
      await fetchGroups();
    } catch (e) {
      console.error(e);
      alert(e.message);
    }
  };

  const deleteGroup = async (groupId) => {
    if (!window.confirm('¿Eliminar este grupo?')) return;
    try {
      await Pantallas.deleteGroup(groupId); // asegúrate de tener este endpoint en tu API
      await fetchGroups();
      if (selected?._id === groupId) {
        setSelected(null);
        setOpen(false);
      }
    } catch (e) {
      console.error(e);
      alert(e.message);
    }
  };

  const refreshSelected = async () => {
    try {
      const data = await Pantallas.getGroups();
      setGroups(Array.isArray(data) ? data : []);
      if (selected?._id) {
        const updated = (Array.isArray(data) ? data : []).find((x) => x._id === selected._id);
        if (updated) setSelected(updated);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const openGroup = async (g) => {
    setSelected(g);
    setOpen(true);
    try {
      await fetchPlaylist(g._id);
    } catch (e) {
      console.error(e);
      alert(e.message);
    }
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  // === Filtrado y ordenamiento ===
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    let xs = groups;
    if (term) xs = xs.filter((g) => g.nombre?.toLowerCase().includes(term));
    switch (sort) {
      case 'name':
        xs = [...xs].sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''));
        break;
      case 'videos':
        xs = [...xs].sort((a, b) => (b.playlist?.length || 0) - (a.playlist?.length || 0));
        break;
      default:
        xs = [...xs].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    }
    return xs;
  }, [groups, q, sort]);

  // === Render ===
  return (
    <div className="min-h-screen bg-[#0a0f1a] text-white">
      {/* Topbar */}
      <header className="sticky top-0 z-30 border-b border-white/10 bg-[#0b1220]/80 backdrop-blur">
        <div className="mx-auto max-w-7xl px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-lg bg-indigo-600 inline-flex items-center justify-center font-bold">
              PG
            </div>
            <div>
              <div className="text-sm text-white/60">Pantallas</div>
              <h1 className="text-base font-semibold">
                {view === 'grupos' ? 'Grupos' : 'Pantallas'}
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 rounded-xl bg-white/5 p-1 border border-white/10">
              <button
                onClick={() => setView('grupos')}
                className={cls(
                  'px-3 py-1.5 rounded-lg text-sm',
                  view === 'grupos' ? 'bg-indigo-600' : 'hover:bg-white/10'
                )}
              >
                Grupos
              </button>
              <button
                onClick={() => setView('pantallas')}
                className={cls(
                  'px-3 py-1.5 rounded-lg text-sm',
                  view === 'pantallas' ? 'bg-indigo-600' : 'hover:bg-white/10'
                )}
              >
                Pantallas
              </button>
            </div>
            {view === 'grupos' && (
              <button
                onClick={() => setShowCreate(true)}
                className="rounded-xl bg-indigo-600 hover:bg-indigo-500 px-3 py-2 text-sm font-medium"
              >
                Nuevo grupo
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Toolbar (grupos) */}
      <div
        className="mx-auto max-w-7xl px-6 py-5"
        style={{ display: view === 'pantallas' ? 'none' : 'block' }}
      >
        <div className="flex flex-wrap items-center gap-3">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar grupo..."
            className="w-full sm:w-80 rounded-xl bg-white/5 border border-white/10 px-3 py-2 outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/60"
          />
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="rounded-xl bg-white/5 border border-white/10 px-3 py-2"
          >
            <option value="recent">Más recientes</option>
            <option value="name">Nombre</option>
            <option value="videos"># Videos</option>
          </select>
        </div>
      </div>

      {/* Grid de grupos */}
      <div
        className="mx-auto max-w-7xl px-6 pb-16"
        style={{ display: view === 'pantallas' ? 'none' : 'block' }}
      >
        {loading ? (
          <div className="text-white/70">Cargando grupos…</div>
        ) : error ? (
          <div className="text-red-400">{error}</div>
        ) : filtered.length === 0 ? (
          <div className="text-white/60">No hay grupos.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((g) => (
              <GroupCard
                key={g._id}
                group={g}
                onOpen={openGroup}
                // Si quieres eliminar desde la tarjeta también:
                // onDelete={deleteGroup}
              />
            ))}
          </div>
        )}
      </div>

      {/* Sección de pantallas */}
      {view === 'pantallas' && <TvsSection groups={groups} />}

      {/* Drawer de administración por grupo */}
      <div className="relative z-40">
        <div className={cls(open ? '' : 'hidden')}>
          <div
            className="fixed inset-0 bg-black/50"
            onClick={() => setOpen(false)}
          />
          <div className="fixed right-0 top-0 h-full w-full max-w-5xl bg-[#0b1220] text-white border-l border-white/10 shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <h3 className="text-lg font-semibold">
                {selected ? `Grupo: ${selected.nombre}` : 'Grupo'}
              </h3>
              <div className="flex items-center gap-2">
                {selected && (
                  <button
                    onClick={() => deleteGroup(selected._id)}
                    className="rounded-lg px-2 py-1 bg-red-600 hover:bg-red-500 text-sm"
                  >
                    Eliminar
                  </button>
                )}
                <button
                  onClick={() => setOpen(false)}
                  className="rounded-lg p-1 hover:bg-white/10"
                >
                  ✕
                </button>
              </div>
            </div>
            <div className="h-[calc(100%-4rem)] overflow-y-auto p-6">
              {selected ? (
                <div className="grid gap-8">
                  <section className="grid gap-3">
                    <h3 className="text-sm font-semibold text-white/80">
                      Playlist
                    </h3>
                    <PlaylistEditor
                      group={selected}
                      items={playlist}
                      setItems={setPlaylist}
                      onRefresh={() => fetchPlaylist(selected._id)}
                    />
                  </section>

                  <section className="grid gap-3">
                    <h3 className="text-sm font-semibold text-white/80">
                      Diseño y estilo
                    </h3>
                    <GroupDesignEditor
                      group={selected}
                      onSaved={refreshSelected}
                    />
                  </section>

                  <section className="grid gap-3">
                    <h3 className="text-sm font-semibold text-white/80">
                      Mensajes
                    </h3>
                    <GroupMessagesEditor
                      group={selected}
                      onSaved={refreshSelected}
                    />
                  </section>

                  <section className="grid gap-3">
                    <h3 className="text-sm font-semibold text-white/80">
                      Información
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-white/80">
                      <div>
                        <span className="text-white/60">ID:</span>{' '}
                        {selected._id}
                      </div>
                      <div>
                        <span className="text-white/60">Creado:</span>{' '}
                        {selected.createdAt
                          ? new Date(selected.createdAt).toLocaleString()
                          : '-'}
                      </div>
                      <div>
                        <span className="text-white/60">Mensajes:</span>{' '}
                        {selected.mensajes?.length ?? 0}
                      </div>
                      <div>
                        <span className="text-white/60">Videos:</span>{' '}
                        {selected.playlist?.length ?? 0}
                      </div>
                    </div>
                  </section>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {/* Modal crear grupo */}
      <Modal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        title="Crear nuevo grupo"
        footer={
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setShowCreate(false)}
              className="rounded-xl px-3 py-2 text-sm bg-slate-700 hover:bg-slate-600"
            >
              Cancelar
            </button>
            <button
              onClick={createGroup}
              className="rounded-xl px-3 py-2 text-sm bg-indigo-600 hover:bg-indigo-500"
            >
              Crear
            </button>
          </div>
        }
      >
        <div className="grid gap-4">
          <input
            value={newGroup.nombre}
            onChange={(e) =>
              setNewGroup((v) => ({ ...v, nombre: e.target.value }))
            }
            placeholder="Nombre"
            className="rounded-xl bg-white/5 border border-white/10 text-white px-3 py-2"
          />
          <div className="grid grid-cols-2 gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={!!newGroup.diseno?.fecha}
                onChange={(e) =>
                  setNewGroup((v) => ({
                    ...v,
                    diseno: { ...v.diseno, fecha: e.target.checked },
                  }))
                }
              />
              Mostrar fecha
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={!!newGroup.diseno?.reloj}
                onChange={(e) =>
                  setNewGroup((v) => ({
                    ...v,
                    diseno: { ...v.diseno, reloj: e.target.checked },
                  }))
                }
              />
              Mostrar reloj
            </label>
          </div>
          <input
            value={newGroup.diseno?.logo || ''}
            onChange={(e) =>
              setNewGroup((v) => ({
                ...v,
                diseno: { ...v.diseno, logo: e.target.value },
              }))
            }
            placeholder="Logo (URL opcional)"
            className="rounded-xl bg-white/5 border border-white/10 text-white px-3 py-2"
          />
        </div>
      </Modal>
    </div>
  );
}
